import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Share2, MoreHorizontal, Info } from "lucide-react";
import {
  Breadcrumb,
  DateRange,
  DateRangeDropdown,
  EmptyRow,
  formatCompact,
  formatCurrency,
  formatRoas,
  fmtShortDate,
  LoadingRow,
  MetricCard,
  presetToDates,
  readMetric,
  StatusBadge,
  TabsRow,
  useCxoResource,
} from "./shared";
import CampaignOverviewTab from "./tabs/campaign-overview-tab";
import CampaignAdsetsTab from "./tabs/campaign-adsets-tab";
import AudienceTab from "./tabs/audience-tab";
import RegionsTab from "./tabs/regions-tab";
import PlacementsTab from "./tabs/placements-tab";

// Campaign detail — GET /cxo-dashboard/campaigns/{id} for the header+cards,
// then one of five tabs (Overview/Ad Sets/Audience/Regions/Placements), each
// its own real endpoint per the doc.

const CampaignDetail: React.FC = () => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState<DateRange>("Last 7 Days");
  const { start_date, end_date } = useMemo(() => presetToDates(dateRange), [dateRange]);
  const baseParams = useMemo(() => ({ mode: "fixed", start_date, end_date }), [start_date, end_date]);
  const [tab, setTab] = useState("overview");

  const { data, loading } = useCxoResource<any>("/cxo-dashboard/campaigns", campaignId, baseParams, !!campaignId);

  if (loading && !data) return <LoadingRow label="Loading campaign…" />;
  if (!data) return <EmptyRow label="Couldn't load this campaign." />;

  const name = data.name ?? data.campaign?.name;
  const status = data.status ?? data.campaign?.status;
  const description = data.description ?? data.campaign?.description;
  const adsetCount = data.adset_count ?? data.campaign?.adset_count;
  const s = data.summary || {};

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "adsets", label: `Ad Sets${adsetCount != null ? ` (${adsetCount})` : ""}` },
    { id: "audience", label: "Audience" },
    { id: "regions", label: "Regions" },
    { id: "placements", label: "Placements" },
  ];

  return (
    <div className="space-y-5">
      <Breadcrumb trail={[{ label: "Campaigns", to: "/cxo/campaigns" }, { label: name || "Campaign" }]} />

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-gray-100">{name}</h1>
            {status && <StatusBadge status={status} />}
          </div>
          {description && <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 max-w-xl">{description}</p>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <DateRangeDropdown value={dateRange} onChange={setDateRange} />
          <button type="button" className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800">
            <Share2 size={14} /> Share
          </button>
          <button type="button" className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800">
            <MoreHorizontal size={16} />
          </button>
        </div>
      </div>

      {data.last_synced_at && (
        <p className="flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500 -mt-2">
          {fmtShortDate(new Date(start_date))} – {fmtShortDate(new Date(end_date))}
          <span className="mx-1">·</span>
          (Last synced: {new Date(data.last_synced_at).toLocaleString()}) <Info size={11} />
        </p>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-8 gap-3">
        <MetricCard label="Spend" metric={readMetric(s.spend)} format={formatCurrency} invert empty="₦0.00" />
        <MetricCard label="Impressions" metric={readMetric(s.impressions)} format={formatCompact} empty="0" />
        <MetricCard label="Reach" metric={readMetric(s.reach)} format={formatCompact} empty="0" />
        <MetricCard label="Results" metric={readMetric(s.results)} format={formatCompact} empty="0" />
        <MetricCard label="Cost per result" metric={readMetric(s.cost_per_result)} format={formatCurrency} invert empty="₦0.00" />
        <MetricCard label="Reported value" metric={readMetric(s.reported_value ?? s.attributed_revenue)} format={formatCurrency} empty="₦0.00" />
        <MetricCard label="ROAS" metric={readMetric(s.roas)} format={formatRoas} empty="N/A" />
        {s.frequency != null && <MetricCard label="Frequency" metric={readMetric(s.frequency)} format={(v) => Number(v).toFixed(1)} empty="0.0" />}
      </div>

      <TabsRow tabs={tabs} active={tab} onChange={setTab} />

      {campaignId && (
        <>
          {tab === "overview" && <CampaignOverviewTab campaignId={campaignId} baseParams={baseParams} />}
          {tab === "adsets" && <CampaignAdsetsTab campaignId={campaignId} baseParams={baseParams} onOpenAdset={(adsetId) => navigate(`/cxo/campaigns/${campaignId}/adsets/${adsetId}`)} />}
          {tab === "audience" && <AudienceTab basePath="/cxo-dashboard/campaigns" entityId={campaignId} baseParams={baseParams} />}
          {tab === "regions" && <RegionsTab basePath="/cxo-dashboard/campaigns" entityId={campaignId} baseParams={baseParams} />}
          {tab === "placements" && <PlacementsTab basePath="/cxo-dashboard/campaigns" entityId={campaignId} baseParams={baseParams} exportPath={`/cxo-dashboard/campaigns/${campaignId}/placements/export`} />}
        </>
      )}
    </div>
  );
};

export default CampaignDetail;
